<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Search } from '@element-plus/icons-vue'
import area from '@/common/json/area.json'
import { handleTreeData } from '@/common/js/common.js'

const store = useStore()

const areaOptions = handleTreeData(area, 'name', 'name')

const formRef = ref()
const searchKeyword = ref('')
const dialogVisible = ref(false)
const dialogMode = ref('add')
const currentPage = ref(1)
const pageSize = ref(10)
const addressList = ref([])
const activeAddressId = ref('')

const formData = reactive({
  value: [],
  address: '',
  consigneeName: '',
  consigneePhone: '',
  email: '',
  zipCode: '',
  remarks: '',
  defaultFlag: '0'
})

const formRules = {
  value: [{ required: true, message: '请选择所属地区', trigger: 'change' }],
  address: [{ required: true, message: '请输入详细地址', trigger: 'blur' }],
  consigneeName: [{ required: true, message: '请输入收货人', trigger: 'blur' }],
  consigneePhone: [{ required: true, message: '请输入收货人电话', trigger: 'blur' }]
}

const isViewMode = computed(() => dialogMode.value === 'view')
const dialogTitle = computed(() =>
  dialogMode.value === 'add' ? '新增收货地址' : dialogMode.value === 'edit' ? '编辑收货地址' : '查看收货地址'
)
const dialogSubtitle = computed(() =>
  isViewMode.value ? '查看当前保存的收货信息' : '请填写完整信息后保存当前收货地址'
)

const filteredAddressList = computed(() => {
  const keyword = searchKeyword.value.trim()
  if (!keyword) {
    return addressList.value
  }
  return addressList.value.filter(item => {
    const fullAddress = [item.province, item.city, item.district, item.address].filter(Boolean).join('')
    return [item.consigneeName, item.consigneePhone, item.address, fullAddress]
      .filter(Boolean)
      .some(value => String(value).includes(keyword))
  })
})

const paginatedAddressList = computed(() => {
  const start = (currentPage.value - 1) * pageSize.value
  return filteredAddressList.value.slice(start, start + pageSize.value)
})

const total = computed(() => filteredAddressList.value.length)

const resetForm = () => {
  formData.value = []
  formData.address = ''
  formData.consigneeName = ''
  formData.consigneePhone = ''
  formData.email = ''
  formData.zipCode = ''
  formData.remarks = ''
  formData.defaultFlag = '0'
  activeAddressId.value = ''
}

const fillForm = data => {
  formData.value = [data.province, data.city, data.district].filter(Boolean)
  formData.address = data.address || ''
  formData.consigneeName = data.consigneeName || ''
  formData.consigneePhone = data.consigneePhone || ''
  formData.email = data.email || ''
  formData.zipCode = data.zipCode || data.postCode || data.postalCode || ''
  formData.remarks = data.remarks || ''
  formData.defaultFlag = data.defaultFlag || '0'
}

const buildPayload = () => ({
  address: formData.address,
  consigneeName: formData.consigneeName,
  consigneePhone: formData.consigneePhone,
  email: formData.email,
  postalCode: formData.zipCode,
  remarks: formData.remarks,
  defaultFlag: formData.defaultFlag,
  province: formData.value[0],
  city: formData.value[1],
  district: formData.value[2]
})

const loadAddressList = async () => {
  const res = await store.dispatch('myMessageModule/userConsigneeList')
  const { code, data = [] } = res?.data || {}
  if (code === '0') {
    addressList.value = Array.isArray(data) ? data : []
    const maxPage = Math.max(1, Math.ceil(addressList.value.length / pageSize.value))
    if (currentPage.value > maxPage) {
      currentPage.value = maxPage
    }
  }
}

const loadAddressDetail = async id => {
  const res = await store.dispatch('myMessageModule/getUserConsignee', id)
  const { code, data = {} } = res?.data || {}
  if (code === '0') {
    fillForm(data)
  }
}

const openDialog = async (mode, row) => {
  dialogMode.value = mode
  resetForm()
  if (row?.id) {
    activeAddressId.value = row.id
    await loadAddressDetail(row.id)
  }
  dialogVisible.value = true
}

const handleAddAddress = () => {
  openDialog('add')
}

const handleEdit = row => {
  openDialog('edit', row)
}

const handleView = row => {
  openDialog('view', row)
}

const closeDialog = () => {
  dialogVisible.value = false
  resetForm()
}

const handleSave = async () => {
  if (isViewMode.value) {
    closeDialog()
    return
  }

  const valid = await formRef.value.validate().catch(() => false)
  if (!valid) {
    return
  }

  const action =
    dialogMode.value === 'add'
      ? 'myMessageModule/addUserConsignee'
      : 'myMessageModule/editUserConsignee'
  const payload =
    dialogMode.value === 'add'
      ? buildPayload()
      : { ...buildPayload(), id: activeAddressId.value }

  const res = await store.dispatch(action, payload)
  if (res?.data?.code === '0') {
    ElMessage.success(dialogMode.value === 'add' ? '新增成功' : '修改成功')
    closeDialog()
    await loadAddressList()
  }
}

const handleDelete = row => {
  ElMessageBox.confirm('是否删除该收货地址信息？', '提示', {
    confirmButtonText: '确认',
    cancelButtonText: '取消',
    type: 'warning'
  })
    .then(async () => {
      const res = await store.dispatch('myMessageModule/deleteUserConsignee', row.id)
      if (res?.data?.code === '0') {
        ElMessage.success('删除成功')
        await loadAddressList()
      }
    })
    .catch(() => {})
}

const handleSizeChange = size => {
  pageSize.value = size
  currentPage.value = 1
}

const handleCurrentChange = page => {
  currentPage.value = page
}

onMounted(() => {
  loadAddressList()
})
</script>

<template>
  <div class="address-page">
    <div class="search-bar">
      <el-input
        v-model="searchKeyword"
        placeholder="请输入收货人或收货地址"
        style="width: 320px"
        size="default"
        clearable
      >
        <template #suffix>
          <el-icon><Search /></el-icon>
        </template>
      </el-input>
      <el-button type="primary" class="add-address-btn" @click="handleAddAddress">
        <el-icon><Plus /></el-icon>
        <span>新增收货地址</span>
      </el-button>
    </div>

    <div class="address-list">
      <template v-if="paginatedAddressList.length">
        <div v-for="item in paginatedAddressList" :key="item.id" class="address-card">
          <div class="address-header">
            <div class="address-title">
              <span class="name">{{ item.consigneeName || '--' }}</span>
              <span v-if="item.defaultFlag === '1'" class="default-tag">默认地址</span>
            </div>
            <div class="address-actions">
              <el-button type="text" class="action-btn" @click="handleEdit(item)">编辑</el-button>
              <el-button type="text" class="action-btn" @click="handleView(item)">查看</el-button>
              <el-button type="text" class="action-btn action-btn--danger" @click="handleDelete(item)">删除</el-button>
            </div>
          </div>

          <div class="address-content">
            <div class="address-row">
              <div class="info-item info-item--name">
                <span class="label">收货人：</span>
                <span class="text">{{ item.consigneeName || '--' }}</span>
              </div>
              <div class="info-item info-item--address">
                <span class="label">地址：</span>
                <span class="text text--wide">
                  {{ [item.province, item.city, item.district, item.address].filter(Boolean).join(' ') || '--' }}
                </span>
              </div>
            </div>
            <div class="address-row">
              <div class="info-item info-item--phone">
                <span class="label">手机：</span>
                <span class="text">{{ item.consigneePhone || '--' }}</span>
              </div>
              <div class="info-item info-item--remark">
                <span class="label">邮箱：</span>
                <span class="text text--wide">{{ item.email || '--' }}</span>
              </div>
            </div>
            <div class="address-row">
              <div class="info-item info-item--phone">
                <span class="label">邮编：</span>
                <span class="text">{{ item.zipCode || item.postCode || item.postalCode || '--' }}</span>
              </div>
              <div class="info-item info-item--remark">
                <span class="label">备注：</span>
                <span class="text text--wide">{{ item.remarks || '--' }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
      <el-empty v-else description="暂无收货地址" />
    </div>

    <div class="pagination-wrapper">
      <el-pagination
        v-model:current-page="currentPage"
        :page-sizes="[10, 20, 50]"
        :page-size="pageSize"
        :total="total"
        layout="prev, pager, next, jumper, ->, sizes"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <el-dialog
      v-model="dialogVisible"
      :title="''"
      width="600px"
      destroy-on-close
      class="address-dialog"
      @close="closeDialog"
    >
      <div class="dialog-body">
        <div class="dialog-panel-title">
          <div class="dialog-panel-title__main">{{ dialogTitle }}</div>
          <div class="dialog-panel-title__sub">{{ dialogSubtitle }}</div>
        </div>
        <el-form ref="formRef" :model="formData" :rules="formRules" label-width="120px" class="dialog-form">
          <el-form-item label="所属地区：" prop="value">
            <el-cascader
              v-model="formData.value"
              :options="areaOptions"
              :props="{ expandTrigger: 'hover' }"
              style="width: 100%"
              :disabled="isViewMode"
            />
          </el-form-item>
          <el-form-item label="详细地址：" prop="address">
            <el-input v-model="formData.address" :disabled="isViewMode" />
          </el-form-item>
          <el-form-item label="收货人：" prop="consigneeName">
            <el-input v-model="formData.consigneeName" :disabled="isViewMode" />
          </el-form-item>
          <el-form-item label="收货人电话：" prop="consigneePhone">
            <el-input v-model="formData.consigneePhone" :disabled="isViewMode" />
          </el-form-item>
          <el-form-item label="邮箱：">
            <el-input v-model="formData.email" :disabled="isViewMode" />
          </el-form-item>
          <el-form-item label="邮编：">
            <el-input v-model="formData.zipCode" :disabled="isViewMode" />
          </el-form-item>
          <el-form-item label="备注：">
            <el-input v-model="formData.remarks" type="textarea" :rows="4" :disabled="isViewMode" />
          </el-form-item>
          <el-form-item label="默认地址：">
            <el-radio-group v-model="formData.defaultFlag" :disabled="isViewMode">
              <el-radio label="0">否</el-radio>
              <el-radio label="1">是</el-radio>
            </el-radio-group>
          </el-form-item>
        </el-form>
      </div>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="closeDialog">取消</el-button>
          <el-button type="primary" @click="handleSave">
            {{ isViewMode ? '关闭' : '确认' }}
          </el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<style scoped lang="less">
.address-page {
  padding: 24px;
  background: #ffffff;
}

.search-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.add-address-btn {
  width: 180px;
  height: 40px;
  font-size: 16px;
}

.address-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.address-card {
  border: 1px solid #e4e7ed;
  border-radius: 4px;
  padding: 16px 20px;
}

.address-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.address-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.name {
  font-size: 18px;
  font-weight: 500;
  color: #333;
}

.default-tag {
  border: 1px solid #f56c6c;
  color: #f56c6c;
  border-radius: 12px;
  padding: 2px 8px;
  font-size: 12px;
}

.address-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.action-btn {
  font-size: 16px;
  color: #409eff;
  padding: 0;
}

.action-btn--danger {
  color: #f56c6c;
}

.address-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.address-row {
  display: flex;
  align-items: center;
  gap: 32px;
}

.info-item--name,
.info-item--phone {
  width: 280px;
  flex: 0 0 280px;
}

.info-item--address,
.info-item--remark {
  flex: 1;
  min-width: 0;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.label {
  font-size: 16px;
  color: #666;
  white-space: nowrap;
}

.text {
  display: inline-block;
  min-width: 220px;
  font-size: 14px;
  line-height: 22px;
  color: #303133;
  word-break: break-all;
}

.text--wide {
  min-width: 320px;
}

.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
}

.dialog-body {
  padding: 4px 8px 0;
}

.dialog-panel-title {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 0 0 16px;
  padding: 0 4px;
}

.dialog-panel-title::before {
  content: '';
  width: 6px;
  height: 44px;
  border-radius: 999px;
  background: linear-gradient(180deg, #48a09c 0%, #7fc7bc 100%);
  flex: none;
}

.dialog-panel-title__main {
  font-size: 20px;
  line-height: 28px;
  font-weight: 600;
  color: #303133;
}

.dialog-panel-title__sub {
  margin-top: 2px;
  font-size: 13px;
  line-height: 20px;
  color: #7a8a88;
}

.dialog-form {
  padding: 20px 20px 8px;
  border: 1px solid #edf2f1;
  border-radius: 12px;
  background: #fbfcfc;
}

.dialog-form :deep(.el-form-item__label) {
  white-space: nowrap;
}

.dialog-form :deep(.el-input__wrapper),
.dialog-form :deep(.el-textarea__inner),
.dialog-form :deep(.el-cascader .el-input__wrapper) {
  box-shadow: 0 0 0 1px #d9e7e5 inset;
  border-radius: 8px;
}

.dialog-form :deep(.el-input__wrapper.is-focus),
.dialog-form :deep(.el-textarea__inner:focus),
.dialog-form :deep(.el-cascader .el-input.is-focus .el-input__wrapper) {
  box-shadow: 0 0 0 1px #48a09c inset;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.dialog-footer :deep(.el-button) {
  min-width: 96px;
}

.address-dialog :deep(.el-dialog) {
  border-radius: 16px;
  overflow: hidden;
}

.address-dialog :deep(.el-dialog__header) {
  margin-right: 0;
  padding: 0;
  border-bottom: none;
  background: transparent;
}

.address-dialog :deep(.el-dialog__body) {
  padding-top: 22px;
}

@media (max-width: 768px) {
  .address-page {
    padding: 20px 16px;
  }

  .search-bar {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .address-card {
    padding: 16px;
  }

  .address-content {
    align-items: flex-start;
    gap: 14px;
  }

  .address-row {
    width: 100%;
    flex-direction: column;
    align-items: flex-start;
    gap: 14px;
  }

  .info-item {
    width: 100%;
    flex: none;
    align-items: flex-start;
    flex-direction: column;
  }

  .address-actions {
    flex-wrap: wrap;
    gap: 12px;
  }

  .text,
  .text--wide {
    min-width: 0;
    width: 100%;
  }
}
</style>
